import { useSyncExternalStore } from "react";
import { useLocation } from "react-router-dom";

import { isStandalonePwa } from "@/lib/notifications/pwaInstall";

// ---------------------------------------------------------------------------
// NAV-5 (2026-09-04) — Zurueck-Pfeil fuer die installierte App
// ---------------------------------------------------------------------------
// `manifest.webmanifest` sagt `display: standalone`: als installierte App
// (Home-Bildschirm, Desktop-Installation) gibt es KEINE Browser-Knoepfe. Der
// einzige Rueckweg war die Brotkrume — sie fuehrt nach OBEN in der
// Hierarchie, nicht ZURUECK im Verlauf (Kalkulation → Vorschau → Projekt →
// Kunde ist ein Verlauf). Marvin, Feedback 26.08.2026.
//
// Regel (`backArrowState`, rein und testbar):
//   - Browser-Tab           → hidden   (die Knoepfe gibt es schon; ein zweiter
//                                        waere ein zweiter Traeger derselben
//                                        Sache)
//   - App, Kaltstart        → disabled (ausgegraut, nicht weg — sonst springt
//                                        das Layout beim ersten Klick)
//   - App, Verlauf da       → enabled
//
// «Vor» bewusst nicht: Vorwaerts-Navigation ist selten und braucht Zustand
// (wurde zurueckgegangen?), den die App nicht zuverlaessig kennt. Alt+←
// funktioniert in der installierten App ohnehin ueber den Browser.

export type BackArrowState = "hidden" | "disabled" | "enabled";

export function backArrowState(input: {
  readonly standalone: boolean;
  readonly historyIndex: number | null;
}): BackArrowState {
  if (!input.standalone) return "hidden";
  return input.historyIndex !== null && input.historyIndex > 0
    ? "enabled"
    : "disabled";
}

const STANDALONE_QUERY = "(display-mode: standalone)";

function subscribeStandalone(onChange: () => void): () => void {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return () => undefined;
  }
  const mql = window.matchMedia(STANDALONE_QUERY);
  mql.addEventListener("change", onChange);
  return () => mql.removeEventListener("change", onChange);
}

function getServerSnapshot(): boolean {
  return false;
}

// react-routers BrowserRouter legt seinen Verlaufs-Index als `idx` in
// `window.history.state` ab (0 = erster Eintrag dieser App-Sitzung; ein
// `replace` behaelt den Index, ein Neuladen auch — der Browser bewahrt den
// State). Ein Eintrag OHNE `idx` stammt nicht von der App (z. B. ein fremder
// Deep-Link-Eintrag) — dann gibt es kein «zurueck in der App».
// `tests/topnav-standalone-back.test.tsx` pinnt diesen Vertrag gegen ein
// Upgrade, das ihn still brechen wuerde.
function readHistoryIndex(): number | null {
  if (typeof window === "undefined") return null;
  const state: unknown = window.history.state;
  if (state === null || typeof state !== "object" || !("idx" in state)) {
    return null;
  }
  const idx = (state as { idx: unknown }).idx;
  return typeof idx === "number" ? idx : null;
}

export function useStandaloneBack(): BackArrowState {
  const standalone = useSyncExternalStore(
    subscribeStandalone,
    isStandalonePwa,
    getServerSnapshot,
  );
  // `useLocation` sorgt dafuer, dass jede Navigation neu rendert — der Index
  // im history.state ist zu diesem Zeitpunkt bereits der neue (react-router
  // schreibt pushState VOR dem Listener-Aufruf; bei POP hat der Browser den
  // State schon gewechselt, bevor popstate feuert).
  useLocation();
  return backArrowState({ standalone, historyIndex: readHistoryIndex() });
}
